import type {ReactNode} from 'react';
import {cx} from './cx';

/**
 * The category filter row, and the only shared component the design package
 * itself had.
 *
 * Selection is single and there is **always exactly one selected, never zero** —
 * "all" is a chip, not the absence of one. A row with nothing active looks
 * broken and a guest cannot tell whether they are seeing the whole menu.
 *
 * `aria-pressed` rather than a radio group: the buttons filter a list that is
 * already on screen, so a toggle is what they are, and radio semantics would
 * promise arrow-key roving focus that this does not implement — promising it
 * without implementing it is worse for a screen-reader user than plainly
 * labelled buttons. (Same reasoning as `LanguageToggle`'s segmented variant;
 * they should agree.)
 *
 * The row scrolls horizontally with no fades, no snap and no scroll affordance,
 * exactly as drawn. At the 48px tap floor it is noticeably taller than the
 * design's 33px — that cost is height, not width, and it is the right trade on a
 * control a guest hits while holding a phone one-handed.
 */
export function ChipRow({
  children,
  label,
  className,
}: {
  children: ReactNode;
  /** Localised group name, for the `<nav>`. Required — an unlabelled nav is noise. */
  label: string;
  className?: string;
}) {
  return (
    <nav aria-label={label} className={cx('pk-chiprow', className)}>
      {children}
    </nav>
  );
}

export function Chip({
  children,
  selected,
  onSelect,
  className,
}: {
  children: ReactNode;
  selected: boolean;
  onSelect: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cx('pk-chip', className)}
    >
      {children}
    </button>
  );
}
